import { useCallback, useReducer } from 'react';

import { useReviewContext } from '@hydra/common/context';
import { getVisibilityForDuda } from '@hydra/utils/reviewStructureHelper';

const mapImportance = {
  1: 'lowImpact',
  2: 'mediumImpact',
  3: 'highImpact'
};

const mapSolvability = {
  1: 'easyEffort',
  2: 'hardEffort',
  3: 'veryHardEffort'
};

const initialState = {
  filters: {
    good: false,
    average: false,
    bad: false,
    neutral: false,
    lowImpact: false,
    mediumImpact: false,
    highImpact: false,
    easyEffort: false,
    hardEffort: false,
    veryHardEffort: false
  },
  statusFilter: false,
  importanceFilter: false,
  solvabilityFilter: false
};

const getStatusActive = ({ good, average, bad, neutral }) => {
  return [good, average, bad, neutral].some(value => value);
};

const reducer = (state, action) => {
  let filters;
  switch (action.type) {
    case 'FILTER':
      filters = {
        ...state.filters,
        [action.status]: !state.filters[action.status]
      };
      return {
        ...state,
        filters,
        statusFilter: getStatusActive(filters)
      };
    case 'FILTER_SELECT':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.items
        },
        [`${action.filterType}Filter`]: Object.values(action.items).some(value => value)
      };
    default:
      return state;
  }
};

export const useReviewFilter = ({ criteria, reviewStructure }) => {
  const [{ filters, statusFilter, importanceFilter, solvabilityFilter }, dispatch] = useReducer(reducer, initialState);
  const { isAssistant, isDudaEditor } = useReviewContext();

  const showCrit = useCallback(({ crit, onpageReviewFilter }) => {
    if (isAssistant && isDudaEditor && !getVisibilityForDuda(crit)) {
      return false;
    }

    if (!criteria[crit] || onpageReviewFilter) {
      return true;
    }

    if (statusFilter && !filters[criteria[crit].status]) {
      return false;
    }
    if (importanceFilter && !filters[mapImportance[criteria[crit].importance]]) {
      return false;
    }
    if (solvabilityFilter && !filters[mapSolvability[criteria[crit].solvability]]) {
      return false;
    }
    return true;
  }, [criteria, filters, statusFilter, importanceFilter, solvabilityFilter, isAssistant, isDudaEditor]);

  const showModule = useCallback(({ module, section, onpageReviewFilter }) => {
    const moduleCrits = reviewStructure[section][module];
    return moduleCrits.some(crit => showCrit({ crit, onpageReviewFilter }));
  }, [reviewStructure, showCrit]);

  const showSection = useCallback(({ section, onpageReviewFilter }) => {
    const sectionModules = reviewStructure[section];
    return sectionModules && Object.keys(sectionModules).some(module => showModule({ module, section, onpageReviewFilter }));
  }, [reviewStructure, showModule]);

  const handleFilter = useCallback(status => {
    return dispatch({ type: 'FILTER', status });
  }, []);

  const handleSelect = useCallback(({ items, filterType }) => {
    return dispatch({ type: 'FILTER_SELECT', items, filterType });
  }, []);

  return {
    handleFilter,
    handleSelect,
    filters,
    showCrit,
    showModule,
    showSection
  };
};

export default useReviewFilter;
